import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import get from 'lodash.get';
import { format } from 'date-fns';
import { GET } from '../../../../service/base-api-service';
import {
  getConsumablesExpiryRows,
  getGasSensorExpiryRows,
  isAsrDatePast,
} from '../../../../utils/asr-service-form-data';

const GREEN = [0, 155, 74];
const PALE_GREEN = [217, 242, 207];
const BLACK = [17, 17, 17];
const OVERDUE = [207, 19, 34];

const safe = (value, fallback = 'Not recorded') =>
  value === null || value === undefined || String(value).trim() === '' ? fallback : String(value);

const formatDate = (value, fallback = 'Not recorded') => {
  if (!value) return fallback;
  const numericValue = Number(value);
  const parsed = new Date(
    Number.isNaN(numericValue)
      ? value
      : numericValue < 100000000000
      ? numericValue * 1000
      : numericValue,
  );
  return Number.isNaN(parsed.getTime()) ? fallback : format(parsed, 'dd/MM/yyyy');
};

const formatCoverDate = (value) => {
  if (!value) return '';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? '' : format(parsed, 'dd MMM yyyy');
};

const getReportDate = (report) =>
  report?.servicedDate || report?.serviceDate || report?.reportDate || report?.createdAt || report?.createdDate || null;

const fetchChamberMeta = async (chamberId) => {
  for (const url of [`v1/admin/chamber/${chamberId}`, `v1/chamber/${chamberId}`]) {
    try {
      const data = await GET(url);
      if (data) return data;
    } catch (err) {
      // Try the next endpoint.
    }
  }
  return {};
};

const fetchLatestServiceForm = async (chamberId, tenantId) => {
  const urls = [
    `v1/admin/report/chambers/${chamberId}/reports?type=after-service`,
    `v1/report/chambers/${chamberId}/reports?type=after-service`,
  ];

  for (const url of urls) {
    try {
      const payload = await GET(url);
      const reports = Array.isArray(payload) ? payload : payload?.results || payload?.list || [];
      if (!reports.length) continue;

      const latest = [...reports].sort(
        (left, right) => new Date(getReportDate(right) || 0).getTime() - new Date(getReportDate(left) || 0).getTime(),
      )[0];
      const serviceFormId = latest?.serviceFormId || latest?.id || latest?.fileName || latest?.name;
      if (!serviceFormId) continue;

      return await GET(
        `v1/admin/report/serviceForm/${serviceFormId}${tenantId ? `?tenantId=${encodeURIComponent(tenantId)}` : ''}`,
      );
    } catch (err) {
      // Try the next endpoint.
    }
  }
  return {};
};

const getChamberFields = (chamber, meta, serviceForm) => ({
  masaNumber: safe(get(meta, 'masaNumber') || get(meta, 'serialNumber') || chamber.chamberName),
  location: safe(get(serviceForm, 'chamberLocation'), 'Not applicable'),
  capacity: safe(get(meta, 'capacity') || get(meta, 'size') || get(meta, 'chamberSize')),
  scrubberType: safe(
    get(meta, 'scrubberTypeEnum.displayName') ||
      get(meta, 'scrubberTypeEnum.name') ||
      get(meta, 'scrubberType') ||
      get(meta, 'scrubberModel'),
  ),
  nextServiceDue: safe(
    get(serviceForm, 'nextServiceDate') ||
      get(serviceForm, 'nextServiceDueDate') ||
      get(serviceForm, 'nextServiceDue') ||
      get(serviceForm, 'service.nextServiceDate') ||
      get(meta, 'nextServiceDate') ||
      get(meta, 'nextServiceDueDate') ||
      get(chamber, 'nextServiceDate') ||
      get(chamber, 'nextServiceDueDate'),
  ),
});

const chunk = (values, size) => {
  const rows = [];
  for (let index = 0; index < values.length; index += size) {
    const row = values.slice(index, index + size);
    while (row.length < size) row.push('');
    rows.push(row);
  }
  return rows;
};

const loadImageData = (src, { maxWidth, mimeType = 'image/png', quality = 0.9 } = {}) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => {
      const scale = maxWidth && image.width > maxWidth ? maxWidth / image.width : 1;
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(image.width * scale);
      canvas.height = Math.round(image.height * scale);
      const context = canvas.getContext('2d');
      context.drawImage(image, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL(mimeType, quality));
    };
    image.onerror = () => reject(new Error(`Unable to load report image: ${src}`));
    image.src = src;
  });

const addCoverPage = (doc, session, coverImage, logoImage) => {
  const details = session.reportDetails || {};
  const pageWidth = doc.internal.pageSize.getWidth();

  doc.addImage(coverImage, 'JPEG', 0, 0, pageWidth, 140, undefined, 'FAST');
  doc.setFillColor(...GREEN);
  doc.rect(0, 140, pageWidth, 6, 'F');

  const left = 26;
  doc.setTextColor(...GREEN);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(31);
  doc.text('MINEARC SYSTEMS', left, 160);

  doc.setTextColor(0, 0, 0);
  doc.setFontSize(31);
  doc.text('Service Report', left, 179);

  const dateOnSite = `${formatCoverDate(session.visitStart)} - ${formatCoverDate(session.visitEnd)}`;
  const reportRows = [
    ['SITE', safe(details.siteName || session.siteName, '')],
    ['ATT', safe(details.attendeeName, '')],
    ['EMAIL', safe(details.attendeeEmail, '')],
  ];
  if (details.ccEmail) reportRows.push(['CC', details.ccEmail]);
  reportRows.push(
    ['REPORT PREPARED BY', safe(details.preparedBy, '')],
    ['EMAIL', safe(details.preparedByEmail, '')],
    ['TEL', safe(details.preparedByTelephone, '')],
    ['DATE ON SITE', dateOnSite],
    ['QUOTE #', safe(details.quoteNumber, '')],
  );

  autoTable(doc, {
    startY: 191,
    margin: { left, right: 62 },
    head: [[{ content: 'REPORT DETAILS', colSpan: 2 }]],
    body: reportRows,
    theme: 'grid',
    headStyles: {
      fillColor: GREEN,
      textColor: [255, 255, 255],
      fontSize: 8,
      fontStyle: 'bold',
      halign: 'left',
      cellPadding: 2,
    },
    bodyStyles: { fontSize: 7.5, textColor: BLACK, lineColor: BLACK, lineWidth: 0.2, cellPadding: 2 },
    columnStyles: {
      0: { fillColor: PALE_GREEN, fontStyle: 'bold', cellWidth: 48 },
      1: { cellWidth: 74 },
    },
  });

  doc.addImage(logoImage, 'PNG', 164, 267, 32, 15.625, undefined, 'FAST');
};

const addFooter = (doc, pageNumber) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  doc.setFillColor(...GREEN);
  doc.rect(0, pageHeight - 9, pageWidth, 9, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.text('www.minearc.com', pageWidth / 2, pageHeight - 3.5, { align: 'center' });
  doc.text(String(pageNumber), pageWidth - 12, pageHeight - 3.5, { align: 'right' });
};

const addPageHeader = (doc) => {
  const margin = 26;
  doc.setTextColor(...BLACK);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.text('MineARC Service Report', margin, 15);
  doc.setDrawColor(...GREEN);
  doc.setLineWidth(0.8);
  doc.line(margin, 18, 184, 18);
};

const addIntroductionPage = (doc) => {
  addPageHeader(doc);
  const left = 26;
  const width = 158;
  let y = 29;

  doc.setTextColor(...BLACK);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  const introduction =
    'The MineARC Systems Service is a detailed inspection and service which is to be performed on your refuge chambers every four (4) months. This report will outline problems you may have with your refuge chamber and solutions provided to eliminate these problems.';
  const introductionLines = doc.splitTextToSize(introduction, width);
  doc.text(introductionLines, left, y);
  y += introductionLines.length * 4.2 + 3;

  doc.setFont('helvetica', 'normal');
  doc.text('Each refuge chamber report will outline the following;', left, y);
  y += 6;
  [
    'Chamber/Location Information.',
    'Problems and Solution.',
    'Required Parts.',
    'Images.',
  ].forEach((item, index) => {
    doc.setTextColor(...GREEN);
    doc.setFont('helvetica', 'bold');
    doc.text(`${index + 1}.`, left + 2, y);
    doc.setTextColor(...BLACK);
    doc.setFont('helvetica', 'normal');
    doc.text(item, left + 9, y);
    y += 6;
  });

  y += 3;
  doc.setTextColor(...BLACK);
  doc.setFont('helvetica', 'normal');
  doc.text('A service will always include the following;', left, y);
  doc.setLineWidth(0.2);
  doc.line(left, y + 1, left + 59, y + 1);
  y += 7;

  [
    'Door seal inspection and replacement.',
    'Portal window check.',
    'Fire extinguisher test date check.',
    'Strobe and siren function and test.',
    'External/internal signage is clean and checked.',
    'Replacement of 2" check valves on the front of the refuge chamber',
    'UPS battery bank assessment.',
    'Air conditioner functionality check.',
    'External compressed air filter elements were replaced.',
    'External compressed air 1" check valve replaced.',
    'Lighting and sensor test.',
    'Internal seats inspected.',
    'Manual process check.',
    'Chemicals were checked for condition and expiration dates were assessed.',
    'Toilet check.',
    'Gas monitoring is in date and fully functional/ calibrated',
    'Oxygen supply check.',
    'Scrubber/controller test.',
    'Mains power load test.',
    'Vacuum test.',
    'Structural check to the body of the chamber.',
  ].forEach((item) => {
    doc.setTextColor(...GREEN);
    doc.setFont('helvetica', 'bold');
    doc.text('•', left + 2, y);
    doc.setTextColor(...BLACK);
    doc.setFont('helvetica', 'normal');
    doc.text(item, left + 8, y);
    y += 5.5;
  });
};

const addChamberPage = (doc, chamber, meta, serviceForm, session) => {
  const fields = getChamberFields(chamber, meta, serviceForm);
  const margin = 18;

  doc.setTextColor(...BLACK);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.text('MineARC Service Report', margin, 15);
  doc.setDrawColor(...GREEN);
  doc.setLineWidth(0.8);
  doc.line(margin, 18, 192, 18);

  doc.setTextColor(...GREEN);
  doc.setFontSize(16);
  doc.text(safe(chamber.chamberName || fields.masaNumber), margin, 29);

  autoTable(doc, {
    startY: 35,
    margin: { left: margin, right: margin },
    body: [
      ['MA ID', fields.masaNumber, 'Location', fields.location],
      ['Size', fields.capacity, 'Scrubber Model', fields.scrubberType],
      [
        'Next Service Due',
        {
          content: formatDate(fields.nextServiceDue),
          styles: isAsrDatePast(fields.nextServiceDue) ? { textColor: OVERDUE, fontStyle: 'bold' } : {},
        },
        '',
        '',
      ],
    ],
    theme: 'grid',
    styles: { fontSize: 8, cellPadding: 2.3, lineColor: BLACK, lineWidth: 0.2, textColor: BLACK },
    columnStyles: {
      0: { fillColor: PALE_GREEN, fontStyle: 'bold', cellWidth: 28 },
      1: { cellWidth: 59 },
      2: { fillColor: PALE_GREEN, fontStyle: 'bold', cellWidth: 33 },
      3: { cellWidth: 54 },
    },
  });

  const voltages = (get(serviceForm, 'consumables.batteryVoltages.recordBatteryVoltages', []) || [])
    .filter((value) => value !== null && value !== undefined && String(value).trim() !== '')
    .map((value) => {
      const numericValue = Number.parseFloat(value);
      return {
        content: String(value),
        styles: Number.isFinite(numericValue) && numericValue > 12.49
          ? { textColor: [56, 158, 13], fontStyle: 'bold' }
          : Number.isFinite(numericValue) && numericValue < 12.5
          ? { textColor: [207, 19, 34], fontStyle: 'bold' }
          : {},
      };
    });
  const voltageRows = voltages.length ? chunk(voltages, 4) : [['No battery voltages recorded', '', '', '']];

  let currentY = doc.lastAutoTable.finalY + 10;
  doc.setTextColor(...BLACK);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  const technicians = chamber.technicianNames?.length ? ` (${chamber.technicianNames.join(', ')})` : '';
  const serviceText = `This refuge chamber was SERVICED by a certified MineARC service technician${technicians} on ${formatDate(session.visitStart)}.`;
  doc.text(doc.splitTextToSize(serviceText, 174), margin, currentY);
  currentY += 8;

  const priorityStyles = {
    HIGH: { fillColor: [255, 241, 240], textColor: [207, 19, 34] },
    MEDIUM: { fillColor: [255, 251, 230], textColor: [212, 136, 6] },
    LOW: { fillColor: [246, 255, 237], textColor: [56, 158, 13] },
  };
  const issueRows = (chamber.issues || []).map((issue) => {
    const priority = String(issue.priority || 'LOW').toUpperCase();
    return [
      {
        content: priority,
        styles: {
          ...(priorityStyles[priority] || priorityStyles.LOW),
          fontStyle: 'bold',
          halign: 'center',
          valign: 'middle',
        },
      },
      `${issue.selectedAnswer || ''}${issue.note ? `\nNote: ${issue.note}` : ''}`,
      String(issue.solution || '').trim() || 'Pending',
    ];
  });

  if (issueRows.length) {
    autoTable(doc, {
      startY: currentY,
      margin: { left: margin, right: margin, bottom: 18 },
      head: [['PRIORITY', 'ISSUE', 'SOLUTION']],
      body: issueRows,
      theme: 'grid',
      headStyles: { fillColor: GREEN, textColor: [255, 255, 255], halign: 'center', fontSize: 8 },
      bodyStyles: { fontSize: 7.5, cellPadding: 2.2, lineColor: BLACK, lineWidth: 0.2, valign: 'top' },
      alternateRowStyles: { fillColor: [241, 241, 241] },
      columnStyles: { 0: { cellWidth: 22 }, 1: { cellWidth: 76 }, 2: { cellWidth: 76 } },
    });
    currentY = doc.lastAutoTable.finalY + 9;
  } else {
    doc.setTextColor(76, 103, 128);
    doc.text('No open issues were recorded for this chamber.', margin, currentY);
    currentY += 9;
  }

  autoTable(doc, {
    startY: currentY,
    margin: { left: margin, right: margin },
    head: [[{ content: 'BATTERY VOLTAGES', colSpan: 4, styles: { halign: 'center' } }]],
    body: voltageRows,
    theme: 'grid',
    headStyles: { fillColor: GREEN, textColor: [255, 255, 255], fontSize: 8, fontStyle: 'bold' },
    bodyStyles: { fontSize: 8, halign: 'center', cellPadding: 2.2, lineColor: BLACK, lineWidth: 0.2 },
  });

  const consumableRows = getConsumablesExpiryRows(serviceForm);
  if (consumableRows.length) {
    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 8,
      margin: { left: margin, right: margin },
      head: [
        [{ content: 'CONSUMABLES EXPIRY LOG', colSpan: 2, styles: { halign: 'center' } }],
        ['CONSUMABLE', 'EXPIRY DATE'].map((content) => ({ content, styles: { fillColor: PALE_GREEN, textColor: BLACK, halign: 'center' } })),
      ],
      body: consumableRows.map((row) => [
        row.consumable,
        {
          content: row.expiryDate,
          styles: isAsrDatePast(row.expiryDateValue) ? { textColor: OVERDUE, fontStyle: 'bold' } : {},
        },
      ]),
      theme: 'grid',
      headStyles: { fillColor: GREEN, textColor: [255, 255, 255], fontSize: 8, fontStyle: 'bold' },
      bodyStyles: { fontSize: 7.5, cellPadding: 2.2, lineColor: BLACK, lineWidth: 0.2 },
      alternateRowStyles: { fillColor: [248, 252, 250] },
      columnStyles: { 0: { cellWidth: 116, fontStyle: 'bold' }, 1: { cellWidth: 58, halign: 'center' } },
    });
  }

  const gasSensorRows = getGasSensorExpiryRows(serviceForm);
  if (gasSensorRows.length) {
    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 8,
      margin: { left: margin, right: margin },
      head: [
        [{ content: 'GAS SENSORS EXPIRY LOG', colSpan: 4, styles: { halign: 'center' } }],
        ['SYSTEM', 'SENSOR', 'CALIBRATION DUE DATE', 'EXPIRY DATE'].map((content) => ({ content, styles: { fillColor: PALE_GREEN, textColor: BLACK, halign: 'center' } })),
      ],
      body: gasSensorRows.map((row) => [
        row.system,
        row.sensor,
        {
          content: row.calibrationDate,
          styles: isAsrDatePast(row.calibrationDateValue) ? { textColor: OVERDUE, fontStyle: 'bold' } : {},
        },
        {
          content: row.expiryDate,
          styles: isAsrDatePast(row.expiryDateValue) ? { textColor: OVERDUE, fontStyle: 'bold' } : {},
        },
      ]),
      theme: 'grid',
      headStyles: { fillColor: GREEN, textColor: [255, 255, 255], fontSize: 8, fontStyle: 'bold' },
      bodyStyles: { fontSize: 7.5, cellPadding: 2.2, lineColor: BLACK, lineWidth: 0.2 },
      alternateRowStyles: { fillColor: [248, 252, 250] },
      columnStyles: { 0: { cellWidth: 48, fontStyle: 'bold' }, 1: { cellWidth: 32 }, 2: { cellWidth: 47, halign: 'center' }, 3: { cellWidth: 47, halign: 'center' } },
    });
  }

};

export const downloadAsrPdf = async (session) => {
  const chambers = session.chambers || [];
  const [coverImage, logoImage, chamberData] = await Promise.all([
    loadImageData('/images/Site_ASR_Cover_Picture.png', {
      maxWidth: 1600,
      mimeType: 'image/jpeg',
      quality: 0.86,
    }),
    loadImageData('/images/MineARC_Logo.png', { maxWidth: 700 }),
    Promise.all(
      chambers.map(async (chamber) => {
        const [fetchedMeta, fetchedServiceForm] = await Promise.all([
          fetchChamberMeta(chamber.chamberId),
          chamber.latestServiceForm
            ? Promise.resolve(chamber.latestServiceForm)
            : fetchLatestServiceForm(chamber.chamberId, session.tenantId),
        ]);
        return {
          meta: { ...chamber, ...fetchedMeta },
          serviceForm: fetchedServiceForm,
        };
      }),
    ),
  ]);

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  addCoverPage(doc, session, coverImage, logoImage);
  doc.addPage();
  addIntroductionPage(doc);
  chambers.forEach((chamber, index) => {
    doc.addPage();
    addChamberPage(doc, chamber, chamberData[index]?.meta || {}, chamberData[index]?.serviceForm || {}, session);
  });
  const pageCount = doc.getNumberOfPages();
  for (let pageNumber = 2; pageNumber <= pageCount; pageNumber += 1) {
    doc.setPage(pageNumber);
    addFooter(doc, pageNumber);
  }

  const safeSiteName = safe(session.siteName, 'Site-ASR')
    .replace(/[\\/:*?"<>|]+/g, '-')
    .replace(/\s+/g, '-');
  doc.save(`MineARC-Site-ASR-${safeSiteName}.pdf`);
};